import React, { useState, useEffect } from 'react';
import { 
  Users, UserPlus, UserCheck, UserX, Search, 
  Filter, MoreVertical, Mail, Phone, Calendar,
  DollarSign, Edit2, Ban, CheckCircle, XCircle, FileText,
  Plus, Trash2
} from 'lucide-react';
import { agentsService, agencyService } from '../../services';
import ApplicationSummaryService from '../../services/applicationSummaryService';
import './AgentManagement.css';

const IDENTITY_BADGES = {
  approved: ['Identity verified', 'bg-green-100 text-green-800'],
  suspected: ['Identity needs review', 'bg-yellow-100 text-yellow-800'],
  denied: ['Identity denied', 'bg-red-100 text-red-800'],
  pending: ['Identity pending', 'bg-gray-100 text-gray-700'],
  expired: ['Identity expired', 'bg-gray-100 text-gray-700'],
  cancelled: ['Identity cancelled', 'bg-gray-100 text-gray-700']
};

const formatMonthDay = (value) => {
  const match = /^(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return 'cleared';
  return new Date(2000, Number(match[1]) - 1, Number(match[2])).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
};

const IdentityBadge = ({ status, method, source, inline }) => {
  if (!status || !IDENTITY_BADGES[status]) return null;
  const [baseLabel, classes] = IDENTITY_BADGES[status];
  const legacyLabel = source === 'veriff' ? 'Prior verification (Veriff, name only) - confirm' : 'Prior verification (mstagents.com) - confirm';
  const label = method === 'legacy' && status === 'approved' ? legacyLabel : baseLabel;
  return <span className={`inline-block ${inline ? 'ml-1' : 'mt-1'} px-2 py-0.5 rounded text-xs font-medium ${classes}`}>{label}</span>;
};

const IdentityDetails = ({ application }) => {
  if (!application.identity_status) return null;
  const veriff = application.identity_legacy_source === 'veriff';
  return (
    <div className="space-y-1">
      <p className="text-sm">
        <span className="font-medium">Identity:</span>{' '}
        <IdentityBadge status={application.identity_status} method={application.identity_method} source={application.identity_legacy_source} inline />
        {application.identity_legacy_scan_ref && (
          <span className="ml-2 text-xs text-gray-500">{veriff ? 'Veriff session ID' : 'iDenfy scan ref'}: {application.identity_legacy_scan_ref}</span>
        )}
      </p>
      {veriff && (
        <p className="text-sm">
          <span className="font-medium">Birthday given (month/day):</span> {formatMonthDay(application.identity_claimed_birth_md)}
          <span className="ml-2 text-xs text-gray-500">(removed automatically 7 days after the request)</span>
        </p>
      )}
    </div>
  );
};

const AgentManagement = () => {
  const [activeTab, setActiveTab] = useState('agents');
  const [agents, setAgents] = useState([]);
  const [applications, setApplications] = useState([]);
  const [deniedApplications, setDeniedApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('total');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showDeniedApplicationModal, setShowDeniedApplicationModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, hasMore: true, total: 0 });
  const [stats, setStats] = useState({
    totalAgents: 0,
    activeAgents: 0,
    pendingAgents: 0,
    pendingApplications: 0,
    deniedApplications: 0,
    eliteAgents: 0
  });
  
  // Alternate emails state
  const [alternateEmails, setAlternateEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [newEmail, setNewEmail] = useState({ email: '', email_type: 'alternate', notes: '' });
  const [emailSearchResults, setEmailSearchResults] = useState([]);

  useEffect(() => {
    // Reset pagination and load fresh data when tab changes
    setPagination({ page: 1, hasMore: true, total: 0 });
    setAgents([]);
    setApplications([]);
    setDeniedApplications([]);
    loadData(true);
  }, [activeTab]);

  // Debounced search effect - trigger search 500ms after user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPagination({ page: 1, hasMore: true, total: 0 });
      setAgents([]);
      setApplications([]);
      setDeniedApplications([]);
      setEmailSearchResults([]);

      // If search term looks like an email, also search alternate emails
      if (searchTerm && searchTerm.includes('@')) {
        searchByEmail(searchTerm);
      }

      loadData(true);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    // Reset pagination and load fresh data when filter changes
    setPagination({ page: 1, hasMore: true, total: 0 });
    setAgents([]);
    setApplications([]);
    setDeniedApplications([]);
    loadData(true);
  }, [selectedFilter]);

  const loadData = async (reset = false) => {
    const currentPage = reset ? 1 : pagination.page;
    
    console.log('🔍 LoadData called:', { activeTab, reset, currentPage });
    
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      if (activeTab === 'agents') {
        // Use agency endpoint for agency-wide agent view with pagination
        const params = {
          page: currentPage,
          limit: 50 // Load more items per page for better UX
        };
        
        // Only add search if it has a value
        if (searchTerm && searchTerm.trim()) {
          params.search = searchTerm.trim();
        }
        
        // Only add filter if it has a value
        if (selectedFilter && selectedFilter !== 'total') {
          params.filter = selectedFilter;
        }
        
        const data = await agencyService.getAgents(params);
        
        if (reset) {
          setAgents(data.agents || []);
        } else {
          setAgents(prev => [...prev, ...(data.agents || [])]);
        }
        
        // Update pagination state
        if (data.pagination) {
          setPagination({
            page: currentPage + 1,
            hasMore: currentPage < data.pagination.pages,
            total: data.pagination.total
          });
        }
        
        // Update stats if provided
        if (data.stats) {
          setStats({
            totalAgents: data.stats.totalAgents || 0,
            activeAgents: data.stats.activeAgents || 0,
            pendingAgents: data.stats.pendingAgents || 0,
            pendingApplications: data.stats.pendingApplications || 0,
            deniedApplications: data.stats.deniedApplications || 0,
            eliteAgents: data.stats.eliteAgents || 0
          });
        }
      } else if (activeTab === 'applications') {
        // Use agency endpoint for pending applications with pagination
        const params = {
          page: currentPage,
          limit: 50
        };
        
        // Only add search if it has a value
        if (searchTerm && searchTerm.trim()) {
          params.search = searchTerm.trim();
        }
        
        const data = await agencyService.getApplications(params);
        
        if (reset) {
          setApplications(data.applications || []);
        } else {
          setApplications(prev => [...prev, ...(data.applications || [])]);
        }
        
        // Update pagination state
        if (data.pagination) {
          setPagination({
            page: currentPage + 1,
            hasMore: currentPage < data.pagination.pages,
            total: data.pagination.total
          });
        }
      } else if (activeTab === 'denied') {
        console.log('🔥 DENIED TAB: Starting to load denied applications');
        
        // Use agency endpoint for denied applications with pagination
        const params = {
          page: currentPage,
          limit: 50
        };
        
        // Only add search if it has a value
        if (searchTerm && searchTerm.trim()) {
          params.search = searchTerm.trim();
        }
        
        console.log('🔥 DENIED TAB: Calling getDeniedApplications with params:', params);
        const data = await agencyService.getDeniedApplications(params);
        console.log('🔥 DENIED TAB: Received data:', data);
        
        if (reset) {
          console.log('🔥 DENIED TAB: Setting denied applications (reset):', data.applications?.length || 0, 'items');
          setDeniedApplications(data.applications || []);
        } else {
          console.log('🔥 DENIED TAB: Appending denied applications:', data.applications?.length || 0, 'items');
          setDeniedApplications(prev => [...prev, ...(data.applications || [])]);
        }
        
        // Update pagination state
        if (data.pagination) {
          console.log('🔥 DENIED TAB: Setting pagination:', data.pagination);
          setPagination({
            page: currentPage + 1,
            hasMore: currentPage < data.pagination.pages,
            total: data.pagination.total
          });
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreData = () => {
    if (!loadingMore && pagination.hasMore) {
      loadData(false);
    }
  };

  // Infinite scroll handler
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && pagination.hasMore && !loadingMore) {
      loadMoreData();
    }
  };

  const handleApproveApplication = async (applicationId) => {
    try {
      const result = await agencyService.approveApplication(applicationId);
      if (result.setupEmailSent) {
        alert('Application approved successfully! A password setup email has been sent to the agent.');
      } else {
        alert('Application approved successfully!');
      }
      loadData();
      setShowApplicationModal(false);
    } catch (error) {
      console.error('Failed to approve application:', error);
      alert('Failed to approve application');
    }
  };

  const handleDenyApplication = async (applicationId, reason) => {
    try {
      await agencyService.denyApplication(applicationId, reason);
      loadData();
      setShowApplicationModal(false);
    } catch (error) {
      console.error('Failed to deny application:', error);
      alert('Failed to deny application');
    }
  };

  const handleUpdateAgentStatus = async (agentId, status) => {
    try {
      await agencyService.updateAgent(agentId, { status });
      loadData();
    } catch (error) {
      console.error('Failed to update agent status:', error);
      alert('Failed to update agent status');
    }
  };

  const handleUpdateCommission = async (agentId, percentage) => {
    try {
      await agencyService.updateAgent(agentId, { commission_percentage: percentage });
      loadData();
      setShowEditModal(false);
    } catch (error) {
      console.error('Failed to update commission:', error);
      alert('Failed to update commission');
    }
  };

  const handleUpdateEliteStatus = async (agentId, isElite) => {
    try {
      await agencyService.updateAgent(agentId, { is_elite: isElite });
      loadData();
    } catch (error) {
      console.error('Failed to update elite status:', error);
      alert('Failed to update elite status');
    }
  };

  // Alternate emails handlers
  const loadAlternateEmails = async (agentId) => {
    setLoadingEmails(true);
    try {
      const response = await agentsService.getAlternateEmails(agentId);
      setAlternateEmails(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to load alternate emails:', error);
      setAlternateEmails([]);
    } finally {
      setLoadingEmails(false);
    }
  };

  const handleAddAlternateEmail = async () => {
    if (!newEmail.email) {
      alert('Please enter an email address');
      return;
    }
    
    try {
      const response = await agentsService.addAlternateEmail(selectedAgent.id, newEmail);
      setAlternateEmails([...alternateEmails, response.email]);
      setNewEmail({ email: '', email_type: 'alternate', notes: '' });
      alert('Alternate email added successfully');
    } catch (error) {
      console.error('Failed to add alternate email:', error);
      alert(error.response?.data?.error || 'Failed to add alternate email');
    }
  };

  const handleUpdateAlternateEmail = async (emailId, updates) => {
    try {
      const response = await agentsService.updateAlternateEmail(selectedAgent.id, emailId, updates);
      setAlternateEmails(alternateEmails.map(e => e.id === emailId ? response.email : e));
      alert('Alternate email updated successfully');
    } catch (error) {
      console.error('Failed to update alternate email:', error);
      alert(error.response?.data?.error || 'Failed to update alternate email');
    }
  };

  const handleDeleteAlternateEmail = async (emailId, email) => {
    if (!confirm(`Are you sure you want to remove the alternate email "${email}"?`)) {
      return;
    }
    
    try {
      await agentsService.deleteAlternateEmail(selectedAgent.id, emailId);
      setAlternateEmails(alternateEmails.filter(e => e.id !== emailId));
      alert('Alternate email removed successfully');
    } catch (error) {
      console.error('Failed to delete alternate email:', error);
      alert(error.response?.data?.error || 'Failed to remove alternate email');
    }
  };

  const searchByEmail = async (email) => {
    console.log('🔍 Searching by email:', email);
    try {
      const results = await agentsService.searchByEmail(email);
      console.log('✅ Email search results:', results);
      setEmailSearchResults(results);
    } catch (error) {
      console.error('❌ Failed to search by email:', error);
      setEmailSearchResults([]);
    }
  };

  const handleDownloadApplicationSummary = async (agent) => {
    try {
      const agentName = agent.name || `${agent.first_name || ''} ${agent.last_name || ''}`.trim();
      await ApplicationSummaryService.downloadPDF(agent.id, agentName);
    } catch (error) {
      console.error('Failed to download application summary:', error);
      if (error.response?.status === 404) {
        alert('Application not found for this agent. They may have been created before the application system was implemented.');
      } else {
        alert('Failed to download application summary. Please try again.');
      }
    }
  };

  const handleApproveDeniedApplication = async (applicationId) => {
    try {
      const result = await agencyService.approveDeniedApplication(applicationId);
      if (result.setupEmailSent) {
        alert('Previously denied application approved successfully! A welcome email has been sent to the agent.');
      } else {
        alert('Previously denied application approved successfully!');
      }
      loadData(true); // Reload all data
      setShowDeniedApplicationModal(false);
    } catch (error) {
      console.error('Failed to approve denied application:', error);
      alert(error.response?.data?.error || 'Failed to approve denied application');
    }
  };

  const handleDeleteDeniedApplication = async (applicationId) => {
    if (!confirm('Are you sure you want to permanently delete this denied application? This will allow the email address to be used for new applications.')) {
      return;
    }

    try {
      await agencyService.deleteDeniedApplication(applicationId);
      alert('Denied application deleted successfully. The email address can now be used for new applications.');
      loadData(true); // Reload all data
      setShowDeniedApplicationModal(false);
    } catch (error) {
      console.error('Failed to delete denied application:', error);
      alert(error.response?.data?.error || 'Failed to delete denied application');
    }
  };

  // When searching by email, prioritize email search results over filter
  let filteredAgents;
  
  if (searchTerm.includes('@') && emailSearchResults.length > 0) {
    // Email search: show all matching agents regardless of filter
    filteredAgents = emailSearchResults;
  } else {
    // Regular search: filter loaded agents by name/email
    filteredAgents = agents.filter(agent =>
      agent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  const filteredApplications = applications.filter(app =>
    `${app.first_name} ${app.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDeniedApplications = deniedApplications.filter(app =>
    `${app.first_name} ${app.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Agent Management</h1>
        <p className="text-gray-600 mt-2">Manage your agency's agents and review applications</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Agents</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalAgents}</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Agents</p>
              <p className="text-2xl font-bold text-green-600">{stats.activeAgents}</p>
            </div>
            <UserCheck className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Applications</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingApplications}</p>
            </div>
            <UserPlus className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Elite Agents</p>
              <p className="text-2xl font-bold text-gray-900">{stats.eliteAgents}</p>
            </div>
            <DollarSign className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('agents')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'agents'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-4 h-4 inline-block mr-2" />
              Active Agents ({agents.length})
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'applications'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <UserPlus className="w-4 h-4 inline-block mr-2" />
              Applications ({applications.length})
            </button>
            <button
              onClick={() => setActiveTab('denied')}
              className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'denied'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <UserX className="w-4 h-4 inline-block mr-2" />
              Denied Applications ({deniedApplications.length})
            </button>
          </nav>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={`Search ${activeTab === 'agents' ? 'agents' : activeTab === 'denied' ? 'denied applications' : 'applications'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Filter Buttons - Only show for agents tab */}
          {activeTab === 'agents' && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedFilter('total')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  selectedFilter === 'total'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Total Agents ({stats.totalAgents})
              </button>
              <button
                onClick={() => setSelectedFilter('active')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  selectedFilter === 'active'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Active Agents ({stats.activeAgents})
              </button>
              <button
                onClick={() => setSelectedFilter('pending')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  selectedFilter === 'pending'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pending ({stats.pendingAgents})
              </button>
              <button
                onClick={() => setSelectedFilter('elite')}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  selectedFilter === 'elite'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Elite ({stats.eliteAgents})
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="loading-spinner"></div>
            </div>
          ) : activeTab === 'agents' ? (
            // Agents Table with Infinite Scroll - Mobile Optimized
            <div className="overflow-x-auto max-h-96 overflow-y-auto" onScroll={handleScroll}>
              {filteredAgents.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No agents found</p>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="hidden md:block">
                    <table className="min-w-full admin-table">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Elite</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAgents.map((agent) => (
                      <tr key={agent.id}>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                            <p className="text-sm text-gray-500">{agent.agency_name}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <p className="text-gray-900 flex items-center">
                              <Mail className="w-4 h-4 mr-1 text-gray-400" />
                              {agent.email}
                            </p>
                            {agent.phone && (
                              <p className="text-gray-500 flex items-center mt-1">
                                <Phone className="w-4 h-4 mr-1 text-gray-400" />
                                {agent.phone}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <DollarSign className="w-4 h-4 text-gray-400 mr-1" />
                            <span className="text-sm font-medium text-gray-900">
                              {agent.commission_percentage}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            agent.is_elite 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {agent.is_elite ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={agent.status}
                            onChange={(e) => handleUpdateAgentStatus(agent.id, e.target.value)}
                            className={`text-xs font-semibold rounded px-2 py-1 border-0 ${
                              agent.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : agent.status === 'commission_only'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            <option value="active">Active</option>
                            <option value="commission_only">Commission Only</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(agent.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setSelectedAgent(agent);
                                loadAlternateEmails(agent.id);
                                setShowEditModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800"
                              title="Edit Agent"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDownloadApplicationSummary(agent)}
                              className="text-purple-600 hover:text-purple-800"
                              title="Download Application Summary"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                            {agent.status === 'active' ? (
                              <button
                                onClick={() => handleUpdateAgentStatus(agent.id, 'inactive')}
                                className="text-red-600 hover:text-red-800"
                                title="Deactivate Agent"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUpdateAgentStatus(agent.id, 'active')}
                                className="text-green-600 hover:text-green-800"
                                title="Activate Agent"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                    </table>
                  </div>
                  
                  {/* Mobile Card View */}
                  <div className="md:hidden space-y-4">
                    {filteredAgents.map((agent) => (
                      <div key={agent.id} className="bg-white border rounded-lg p-4 shadow-sm">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{agent.name}</h3>
                            <p className="text-sm text-gray-500">{agent.agency_name}</p>
                          </div>
                          <div className="flex items-center space-x-1">
                            {agent.elite_status === 'Elite' && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                ⭐ Elite
                              </span>
                            )}
                            <select
                              value={agent.status}
                              onChange={(e) => handleUpdateAgentStatus(agent.id, e.target.value)}
                              className={`text-xs font-semibold rounded px-2 py-1 border-0 ${
                                agent.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : agent.status === 'commission_only'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              <option value="active">✓ Active</option>
                              <option value="commission_only">💰 Commission Only</option>
                              <option value="inactive">✗ Inactive</option>
                            </select>
                          </div>
                        </div>
                        
                        <div className="space-y-2 mb-3">
                          <div className="flex items-center text-sm text-gray-600">
                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="truncate">{agent.email}</span>
                          </div>
                          {agent.phone && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone className="w-4 h-4 mr-2 text-gray-400" />
                              <span>{agent.phone}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center text-gray-600">
                              <DollarSign className="w-4 h-4 mr-1 text-gray-400" />
                              <span className="font-medium">{agent.commission_rate}%</span>
                            </div>
                            <div className="flex items-center text-gray-500">
                              <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                              <span>{new Date(agent.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex justify-end space-x-2 pt-2 border-t">
                          <button
                            onClick={() => {
                              setSelectedAgent(agent);
                              loadAlternateEmails(agent.id);
                              setShowEditModal(true);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                            title="Edit Agent"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadApplicationSummary(agent)}
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-md"
                            title="Download Application Summary"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          {agent.status === 'active' ? (
                            <button
                              onClick={() => handleUpdateAgentStatus(agent.id, 'inactive')}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                              title="Deactivate Agent"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleUpdateAgentStatus(agent.id, 'active')}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-md"
                              title="Activate Agent"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Loading indicator for infinite scroll */}
                  {loadingMore && (
                    <div className="flex justify-center py-4">
                      <div className="loading-spinner"></div>
                    </div>
                  )}
                  {/* Show total count */}
                  <div className="text-center py-2 text-sm text-gray-500">
                    Showing {agents.length} of {pagination.total} agents
                    {pagination.hasMore && " (scroll for more)"}
                  </div>
                </>
              )}
            </div>
          ) : activeTab === 'applications' ? (
            // Applications List with Infinite Scroll
            <div className="space-y-4 max-h-96 overflow-y-auto" onScroll={handleScroll}>
              {applications.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No pending applications</p>
              ) : (
                <>
                  {applications.map((application) => (
                  <div key={application.id} className="bg-gray-50 rounded-lg p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {application.first_name} {application.last_name}
                        </h3>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-gray-600 flex items-center">
                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                            {application.email}
                          </p>
                          <p className="text-sm text-gray-600 flex items-center">
                            <Phone className="w-4 h-4 mr-2 text-gray-400" />
                            {application.phone}
                          </p>
                          <p className="text-sm text-gray-600 flex items-center">
                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                            Applied {new Date(application.submitted_at).toLocaleDateString()}
                          </p>
                          <IdentityBadge status={application.identity_status} method={application.identity_method} source={application.identity_legacy_source} />
                        </div>
                        {application.address && (
                          <p className="text-sm text-gray-500 mt-2">
                            {application.address}, {application.city}, {application.state} {application.zip_code}
                          </p>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => {
                            setSelectedApplication(application);
                            setShowApplicationModal(true);
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Review
                        </button>
                      </div>
                    </div>
                  </div>
                  ))}
                  {/* Loading indicator for infinite scroll */}
                  {loadingMore && (
                    <div className="flex justify-center py-4">
                      <div className="loading-spinner"></div>
                    </div>
                  )}
                  {/* Show total count */}
                  <div className="text-center py-2 text-sm text-gray-500">
                    Showing {applications.length} of {pagination.total} applications
                    {pagination.hasMore && " (scroll for more)"}
                  </div>
                </>
              )}
            </div>
          ) : activeTab === 'denied' ? (
            // Denied Applications List with Infinite Scroll
            <div className="space-y-4 max-h-96 overflow-y-auto" onScroll={handleScroll}>
              {deniedApplications.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No denied applications</p>
              ) : (
                <>
                  {deniedApplications.map((application) => (
                  <div key={application.id} className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {application.first_name} {application.last_name}
                          </h3>
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Denied
                          </span>
                        </div>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-gray-600 flex items-center">
                            <Mail className="w-4 h-4 mr-2 text-gray-400" />
                            {application.email}
                          </p>
                          <p className="text-sm text-gray-600 flex items-center">
                            <Phone className="w-4 h-4 mr-2 text-gray-400" />
                            {application.phone}
                          </p>
                          <p className="text-sm text-gray-600 flex items-center">
                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                            Applied {new Date(application.submitted_at).toLocaleDateString()}
                          </p>
                          <IdentityBadge status={application.identity_status} method={application.identity_method} source={application.identity_legacy_source} />
                          {application.reviewed_at && (
                            <p className="text-sm text-gray-600 flex items-center">
                              <XCircle className="w-4 h-4 mr-2 text-red-400" />
                              Denied {new Date(application.reviewed_at).toLocaleDateString()}
                              {application.reviewed_by_name && ` by ${application.reviewed_by_name}`}
                            </p>
                          )}
                          {application.denial_reason && (
                            <p className="text-sm text-red-600 mt-2">
                              <strong>Reason:</strong> {application.denial_reason}
                            </p>
                          )}
                        </div>
                        {application.address && (
                          <p className="text-sm text-gray-500 mt-2">
                            {application.address}, {application.city}, {application.state} {application.zip_code}
                          </p>
                        )}
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => {
                            setSelectedApplication(application);
                            setShowDeniedApplicationModal(true);
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Manage
                        </button>
                      </div>
                    </div>
                  </div>
                  ))}
                  {/* Loading indicator for infinite scroll */}
                  {loadingMore && (
                    <div className="flex justify-center py-4">
                      <div className="loading-spinner"></div>
                    </div>
                  )}
                  {/* Show total count */}
                  <div className="text-center py-2 text-sm text-gray-500">
                    Showing {deniedApplications.length} of {pagination.total} denied applications
                    {pagination.hasMore && " (scroll for more)"}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Please select a tab to view content.</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Agent Modal */}
      {showEditModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Edit Agent Settings</h2>
            <div className="space-y-6">
              {/* Business Settings */}
              <div className="border-b pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Business Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Commission Percentage
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      defaultValue={selectedAgent.commission_percentage}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      id="commission-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Elite Agent
                    </label>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        defaultChecked={selectedAgent.is_elite}
                        onChange={(e) => handleUpdateEliteStatus(selectedAgent.id, e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-600">Is Elite Agent</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Management */}
              <div className="border-b pb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Account Management</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="email"
                        defaultValue={selectedAgent.email}
                        className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        id="email-input"
                      />
                      <button
                        onClick={async () => {
                          const newEmail = document.getElementById('email-input').value;
                          if (!newEmail || newEmail === selectedAgent.email) {
                            alert('Please enter a new email address');
                            return;
                          }
                          if (!confirm(`Are you sure you want to change the email from ${selectedAgent.email} to ${newEmail}?\n\nNotifications will be sent to both email addresses.`)) {
                            return;
                          }
                          try {
                            const result = await agencyService.updateAgentEmail(selectedAgent.id, newEmail);
                            alert(result.message || 'Email updated successfully');
                            loadData();
                            setShowEditModal(false);
                          } catch (error) {
                            alert(error.response?.data?.error || 'Failed to update email');
                          }
                        }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                      >
                        Update
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Agent will receive notifications at both old and new email addresses</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password Reset
                    </label>
                    <button
                      onClick={async () => {
                        if (!confirm(`Send password reset email to ${selectedAgent.email}?\n\nThe agent will receive a link to set a new password.`)) {
                          return;
                        }
                        try {
                          const result = await agencyService.sendPasswordReset(selectedAgent.id);
                          alert(result.message || 'Password reset email sent successfully');
                        } catch (error) {
                          alert(error.response?.data?.error || 'Failed to send password reset email');
                        }
                      }}
                      className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center justify-center"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Password Reset Email
                    </button>
                    <p className="text-xs text-gray-500 mt-1">Agent will receive an email with a link to reset their password (expires in 24 hours)</p>
                  </div>
                </div>
              </div>

              {/* Alternate Email Addresses */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Alternate Email Addresses</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Add additional email addresses for commission tracking (e.g., payment emails, old accounts). 
                  These help identify the agent when reconciling payments from Veem or other sources.
                </p>

                {/* Primary Email Display */}
                <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium text-blue-700 uppercase">Primary Email (Login)</span>
                      <p className="text-sm text-gray-900 mt-1">{selectedAgent.email}</p>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded">Primary</span>
                  </div>
                </div>

                {/* Existing Alternate Emails */}
                {loadingEmails ? (
                  <div className="text-center py-4 text-gray-500">Loading emails...</div>
                ) : alternateEmails.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {alternateEmails.map((email) => (
                      <div key={email.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">{email.email}</span>
                            </div>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <select
                                value={email.email_type}
                                onChange={(e) => handleUpdateAlternateEmail(email.id, { email_type: e.target.value, notes: email.notes })}
                                className="px-2 py-1 border rounded text-xs"
                              >
                                <option value="payment">Payment Email</option>
                                <option value="old_account">Old Account</option>
                                <option value="alternate">Alternate</option>
                                <option value="other">Other</option>
                              </select>
                              {email.notes && <span>• {email.notes}</span>}
                              {email.added_by_name && <span>• Added by {email.added_by_name}</span>}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteAlternateEmail(email.id, email.email)}
                            className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Remove email"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm mb-4">
                    No alternate emails added yet
                  </div>
                )}

                {/* Add New Email Form */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Add New Email</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
                      <input
                        type="email"
                        value={newEmail.email}
                        onChange={(e) => setNewEmail({ ...newEmail, email: e.target.value })}
                        placeholder="email@example.com"
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={newEmail.email_type}
                        onChange={(e) => setNewEmail({ ...newEmail, email_type: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="payment">Payment Email (Veem, etc.)</option>
                        <option value="old_account">Old Account Email</option>
                        <option value="alternate">Alternate Email</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Notes (Optional)</label>
                      <input
                        type="text"
                        value={newEmail.notes}
                        onChange={(e) => setNewEmail({ ...newEmail, notes: e.target.value })}
                        placeholder="e.g., Used for Veem payments"
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      onClick={handleAddAlternateEmail}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center text-sm"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Email Address
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
              <button
                onClick={() => {
                  const percentage = document.getElementById('commission-input').value;
                  handleUpdateCommission(selectedAgent.id, percentage);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Application Review Modal */}
      {showApplicationModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Review Application</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Personal Information</h3>
                  <div className="space-y-2">
                    <p className="text-sm"><span className="font-medium">Name:</span> {selectedApplication.first_name} {selectedApplication.last_name}</p>
                    <p className="text-sm"><span className="font-medium">Email:</span> {selectedApplication.email}</p>
                    <p className="text-sm"><span className="font-medium">Phone:</span> {selectedApplication.phone}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Address</h3>
                  <div className="space-y-2">
                    <p className="text-sm">{selectedApplication.address}</p>
                    <p className="text-sm">{selectedApplication.city}, {selectedApplication.state} {selectedApplication.zip_code}</p>
                    <p className="text-sm"><span className="font-medium">Country:</span> {selectedApplication.country}</p>
                  </div>
                </div>
              </div>
              {selectedApplication.identity_status && (
                <div className="mt-6">
                  <h3 className="font-medium text-gray-900 mb-2">Identity Verification</h3>
                  <IdentityDetails application={selectedApplication} />
                </div>
              )}
              <div className="mt-6">
                <h3 className="font-medium text-gray-900 mb-2">Tax Form Requirement</h3>
                <p className="text-sm">
                  This agent will need to complete a <span className="font-medium">{selectedApplication.tax_form_type}</span> form after approval.
                </p>
              </div>
            </div>
            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShowApplicationModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const reason = prompt('Reason for denial (optional):');
                  handleDenyApplication(selectedApplication.id, reason);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Deny
              </button>
              <button
                onClick={() => handleApproveApplication(selectedApplication.id)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Denied Application Management Modal */}
      {showDeniedApplicationModal && selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold flex items-center">
                <UserX className="w-5 h-5 mr-2 text-red-500" />
                Manage Denied Application
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Personal Information</h3>
                  <div className="space-y-2">
                    <p className="text-sm"><span className="font-medium">Name:</span> {selectedApplication.first_name} {selectedApplication.last_name}</p>
                    <p className="text-sm"><span className="font-medium">Email:</span> {selectedApplication.email}</p>
                    <p className="text-sm"><span className="font-medium">Phone:</span> {selectedApplication.phone}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Application Status</h3>
                  <div className="space-y-2">
                    <p className="text-sm"><span className="font-medium">Applied:</span> {new Date(selectedApplication.submitted_at).toLocaleDateString()}</p>
                    <IdentityDetails application={selectedApplication} />
                    {selectedApplication.reviewed_at && (
                      <p className="text-sm"><span className="font-medium">Denied:</span> {new Date(selectedApplication.reviewed_at).toLocaleDateString()}</p>
                    )}
                    {selectedApplication.reviewed_by_name && (
                      <p className="text-sm"><span className="font-medium">Reviewed by:</span> {selectedApplication.reviewed_by_name}</p>
                    )}
                  </div>
                </div>
              </div>
              
              {selectedApplication.address && (
                <div className="mt-6">
                  <h3 className="font-medium text-gray-900 mb-2">Address</h3>
                  <div className="space-y-2">
                    <p className="text-sm">{selectedApplication.address}</p>
                    <p className="text-sm">{selectedApplication.city}, {selectedApplication.state} {selectedApplication.zip_code}</p>
                    <p className="text-sm"><span className="font-medium">Country:</span> {selectedApplication.country}</p>
                  </div>
                </div>
              )}

              {selectedApplication.denial_reason && (
                <div className="mt-6">
                  <h3 className="font-medium text-gray-900 mb-2">Denial Reason</h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">{selectedApplication.denial_reason}</p>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <h3 className="font-medium text-gray-900 mb-2">Tax Form Requirement</h3>
                <p className="text-sm">
                  This agent will need to complete a <span className="font-medium">{selectedApplication.tax_form_type}</span> form after approval.
                </p>
              </div>

              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">Management Options</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li><strong>Approve:</strong> Creates an agent account and sends welcome email</li>
                  <li><strong>Delete:</strong> Permanently removes the application and allows the email to be used for new applications</li>
                </ul>
              </div>
            </div>
            <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeniedApplicationModal(false)}
                className="px-4 py-2 text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteDeniedApplication(selectedApplication.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Delete Application
              </button>
              <button
                onClick={() => handleApproveDeniedApplication(selectedApplication.id)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve Application
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentManagement;
